import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { StockService } from '../../../shared/services/stock.service';
import { ImportArrivalsResponse } from '../../../shared/models/importArrivalsResponse';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

@Component({
  selector: 'tkc-import-arrivals-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, TranslatePipe, MatProgressSpinner],
  templateUrl: './import-arrivals-modal.component.html',
  styleUrls: ['./import-arrivals-modal.component.scss']
})
export class ImportArrivalsModalComponent implements OnInit {
  @ViewChild('fileInput') fileInput: any;

  selectedFile?: File;
  isDragOver = false;
  isUploading = false;
  uploadError?: string;
  importResult?: ImportArrivalsResponse;

  exampleContent: string = [
    'MINE;CAI;TIRESPEC;DATE;QUANTITY',
    'MY MINE 1;123456;;12/31/2099;5',
    'MY MINE 2;;MY TIRE SPEC FULL 40.00R57 ETC;12/31/2199;12'
  ].join('\n');

  constructor(
    public dialogRef: MatDialogRef<ImportArrivalsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { siteId: number },
    private readonly stockService: StockService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
  }

  onFileSelected(event: Event) {
    if (this.isUploading) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.uploadError = undefined;
      this.importResult = undefined;
    }
  }

  onDrop(event: DragEvent) {
    if (this.isUploading) {
      return;
    }

    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      this.uploadError = undefined;
      this.importResult = undefined;
    }
  }

  onDragOver(event: DragEvent) {
    if (this.isUploading) {
      return;
    }

    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave() {
    if (this.isUploading) {
      return;
    }

    this.isDragOver = false;
  }

  import() {
    if (!this.selectedFile || this.isUploading) {
      return;
    }

    this.isUploading = true;
    this.dialogRef.disableClose = true;
    this.uploadError = undefined;
    this.importResult = undefined;

    this.stockService.importArrivals(this.data.siteId, this.selectedFile).subscribe({
      next: (res) => {
        this.importResult = res;

        const errorCount = res.errorLineCount
        const importedCount = res.successLineCount;

        if (errorCount > 0) {
          this.clearSelectedFile();
          return;
        }

        this.dialogRef.close({
          success: true,
          importedCount
        });
      },
      error: (err) => {
        this.uploadError = err?.error?.message ?? this.translate.instant('stock.importArrivals.importFailed');
        this.clearSelectedFile();
      },
      complete: () => {
        this.isUploading = false;
        this.dialogRef.disableClose = false;
      }
    });
  }

  cancel() {
    if (this.isUploading) {
      return;
    }

    this.dialogRef.close({
      success: false
    });
  }

  private clearSelectedFile(): void {
    this.selectedFile = undefined;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
